import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useLexware } from '../../context/LexwareContext';
import CustomDatePicker from '../../components/common/CustomDatePicker';
import { startOfMonth, endOfMonth, eachDayOfInterval, subYears, format, isAfter } from 'date-fns';
import { de } from 'date-fns/locale';

const { FiCalendar } = FiIcons;

function RevenueChart() {
  const { data } = useLexware();
  
  // Default to current month
  const [dateRange, setDateRange] = useState({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date())
  });

  // Daten filtern und aufbereiten
  const chartData = useMemo(() => {
    const sales = data.sales || [];
    
    if (!dateRange.from || !dateRange.to) return [];
    
    // Sicherheitscheck: Start darf nicht nach Ende sein
    if (isAfter(dateRange.from, dateRange.to)) return [];

    // 1. Performance: Lookup-Map für alle Umsätze erstellen (O(N))
    // Wir mappen 'YYYY-MM-DD' -> Summe Netto
    const salesMap = sales.reduce((acc, sale) => {
      const dateKey = format(new Date(sale['RG-Datum']), 'yyyy-MM-dd');
      acc[dateKey] = (acc[dateKey] || 0) + (sale._netto || 0);
      return acc;
    }, {});

    // 2. Kontinuierliche Zeitachse für den gewählten Zeitraum generieren
    // Dies füllt Lücken (Tage ohne Umsatz) mit 0 auf
    let days = [];
    try {
      days = eachDayOfInterval({ start: dateRange.from, end: dateRange.to });
    } catch (e) {
      console.error("Ungültiges Datumsintervall", e);
      return [];
    }

    // 3. Datenpunkte mappen: Aktuelles Jahr vs. Vorjahr
    return days.map(day => {
      const currentKey = format(day, 'yyyy-MM-dd');
      const prevYearDay = subYears(day, 1);
      const prevKey = format(prevYearDay, 'yyyy-MM-dd');

      return {
        dateStr: format(day, 'dd.MM.yyyy', { locale: de }),
        fullDate: day,
        revenue: salesMap[currentKey] || 0,
        revenuePrev: salesMap[prevKey] || 0
      };
    });
  }, [data.sales, dateRange]);

  const option = {
    title: {
      text: '', 
      left: 'center'
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderColor: '#e5e7eb',
      textStyle: {
        color: '#111827',
        fontSize: 12
      },
      formatter: function(params) {
        if (!params || params.length === 0) return '';
        const dateLabel = params[0].name;
        let result = `<div class="font-bold mb-1">${dateLabel}</div>`;
        
        params.forEach(item => {
          const val = item.value !== undefined ? item.value : 0;
          const formattedVal = val.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          result += `
            <div class="flex items-center justify-between gap-4 text-xs">
              <span class="flex items-center gap-1">
                <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background-color:${item.color};"></span>
                ${item.seriesName}
              </span>
              <span class="font-bold font-mono">€${formattedVal}</span>
            </div>
          `;
        });
        return result;
      }
    },
    legend: {
      data: ['Umsatz (Netto)', 'Vorjahr'],
      bottom: 0,
      icon: 'circle',
      itemGap: 20
    },
    xAxis: {
      type: 'category',
      data: chartData.map(item => item.dateStr),
      axisLabel: {
        rotate: 45,
        fontSize: 10,
        color: '#6b7280'
      },
      axisLine: {
        lineStyle: {
          color: '#e5e7eb'
        }
      }
    },
    yAxis: {
      type: 'value',
      axisLabel: {
        formatter: (value) => `€${value}`,
        color: '#6b7280'
      },
      splitLine: {
        lineStyle: {
          color: '#f3f4f6'
        }
      }
    },
    series: [
      {
        name: 'Umsatz (Netto)',
        type: 'line',
        data: chartData.map(item => item.revenue),
        smooth: true,
        showSymbol: false,
        symbolSize: 6,
        lineStyle: {
          color: '#3b82f6',
          width: 3
        },
        itemStyle: {
          color: '#3b82f6'
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(59, 130, 246, 0.3)' },
              { offset: 1, color: 'rgba(59, 130, 246, 0.0)' }
            ]
          }
        }
      },
      {
        name: 'Vorjahr',
        type: 'line',
        data: chartData.map(item => item.revenuePrev),
        smooth: true,
        showSymbol: false,
        lineStyle: {
          color: '#ef4444', // Rot
          width: 2,
          type: 'dashed' // Gestrichelt zur besseren Unterscheidung
        },
        itemStyle: {
          color: '#ef4444'
        },
        // Keine Füllung (areaStyle) für Vorjahr, damit es übersichtlich bleibt
      }
    ],
    grid: {
      left: '3%',
      right: '4%',
      bottom: '15%', // Mehr Platz für Legende und schräge Labels
      top: '5%',
      containLabel: true
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
    >
      <div className="flex flex-col sm:flex-row items-center justify-between mb-6 gap-4">
        <h3 className="text-lg font-bold text-gray-900">Umsatzentwicklung (Netto)</h3>
        {/* New Custom Date Picker */}
        <CustomDatePicker 
          selectedDateRange={dateRange} 
          onChange={setDateRange}
          className="z-20"
        />
      </div>

      {chartData.length > 0 ? (
        <ReactECharts option={option} style={{ height: '350px' }} />
      ) : (
        <div className="h-[350px] flex flex-col items-center justify-center text-gray-400">
          <SafeIcon icon={FiCalendar} className="text-4xl mb-2 opacity-50" />
          <p>Keine Daten für diesen Zeitraum verfügbar</p>
        </div>
      )}
    </motion.div>
  );
}

export default RevenueChart;